import { queryOptions, useQuery } from '@tanstack/react-query';
import type { ScmProviderId } from '@/types/scm-provider';
import { inferScmProviderFromRepoUrl } from '@/types/scm-provider';
import { scmPost } from './scmClient';

export interface BuildDeployment {
  stageName: string;
  state: string;
  result: string;
  startTime: string;
  finishTime: string;
}

export interface Build {
  buildId: number;
  buildNumber: string;
  pipelineId: number;
  pipelineName: string;
  commitId: string;
  repositoryUrl: string;
  sourceBranch: string;
  result: 'succeeded' | 'failed' | 'canceled' | 'partiallySucceeded';
  status: string;
  finishTime: string;
  startTime: string;
  queueTime: string;
  durationMinutes: number;
  requestedBy: string;
  buildUrl: string;
  deployments: BuildDeployment[];
  hasDeployments: boolean;
}

export interface BuildListRequest {
  organization: string;
  project: string;
  repositoryId: string;
  repository?: string;
  branchName?: string;
  workflowId?: string;
  minTime: string;
  maxTime: string;
  maxBuilds?: number;
  provider?: ScmProviderId;
  repoUrl?: string;
}

export interface BuildListResponse {
  success: boolean;
  totalBuilds: number;
  builds?: Build[];
}

export const getBuildList = (
  params: BuildListRequest,
  patToken: string,
): Promise<BuildListResponse> => {
  const provider =
    params.provider ?? inferScmProviderFromRepoUrl(params.repoUrl ?? '');

  return scmPost<BuildListResponse>(
    provider,
    '/api/scm/builds/list',
    patToken,
    {
      organization: params.organization,
      project: params.project,
      repositoryId: params.repositoryId,
      repository: params.repository ?? params.repositoryId,
      branchName: params.branchName,
      workflowId: params.workflowId,
      minTime: params.minTime,
      maxTime: params.maxTime,
      maxBuilds: params.maxBuilds,
    },
  );
};

export const getBuildListQueryOptions = (
  params: BuildListRequest,
  patToken: string,
) => {
  const provider =
    params.provider ?? inferScmProviderFromRepoUrl(params.repoUrl ?? '');
  const repositoryRef = params.repositoryId || params.repository || '';

  return queryOptions({
    queryKey: [
      'build-list',
      provider,
      params.organization,
      repositoryRef,
      params.workflowId,
      params.branchName,
      params.minTime,
    ],
    queryFn: () => getBuildList(params, patToken),
    enabled:
      !!patToken &&
      !!params.organization &&
      !!repositoryRef &&
      !!params.minTime,
    refetchOnWindowFocus: false,
    retry: false,
  });
};

export const useGetBuildList = (params: BuildListRequest, patToken: string) => {
  return useQuery({
    ...getBuildListQueryOptions(params, patToken),
  });
};
